"use client"

import { KeyInsight } from "../../components/key-insight"

interface AgentCardProps {
  name: string
  role: string
  description: string
  capabilities: string[]
  color: "teal" | "sage" | "amber" | "coral" | "slate"
}

const colorClasses = {
  teal: "border-teal-600/30 bg-gradient-to-br from-teal-600/10 to-teal-600/5",
  sage: "border-emerald-700/30 bg-gradient-to-br from-emerald-700/10 to-emerald-700/5",
  amber: "border-amber-600/30 bg-gradient-to-br from-amber-600/10 to-amber-600/5",
  coral: "border-rose-600/30 bg-gradient-to-br from-rose-600/10 to-rose-600/5",
  slate: "border-slate-500/30 bg-gradient-to-br from-slate-500/10 to-slate-500/5",
}

const dotColors = {
  teal: "bg-teal-500",
  sage: "bg-emerald-600",
  amber: "bg-amber-500",
  coral: "bg-rose-500",
  slate: "bg-slate-400",
}

function AgentCard({ name, role, description, capabilities, color }: AgentCardProps) {
  return (
    <div className={`p-6 rounded-xl border ${colorClasses[color]} transition-all duration-300 hover:scale-[1.02]`}>
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-3 h-3 rounded-full ${dotColors[color]} shadow-lg`} />
        <h4 className="font-bold text-foreground text-lg">{name}</h4>
        <span className="text-xs text-muted-foreground px-2.5 py-1 rounded-full bg-muted/80 font-medium">{role}</span>
      </div>
      <p className="text-sm text-muted-foreground leading-relaxed mb-5">{description}</p>
      <div className="flex flex-wrap gap-2">
        {capabilities.map((cap, i) => (
          <span key={i} className="text-xs px-2.5 py-1.5 rounded-lg bg-background/80 text-muted-foreground border border-border/50">
            {cap}
          </span>
        ))}
      </div>
    </div>
  )
}

interface MetricCardProps {
  value: string
  label: string
  sublabel?: string
}

function MetricCard({ value, label, sublabel }: MetricCardProps) {
  return (
    <div className="text-center p-6 rounded-xl bg-gradient-to-br from-card/80 to-card/40 border border-border/50">
      <div className="text-3xl sm:text-4xl font-bold text-foreground mb-2">{value}</div>
      <div className="text-sm font-medium text-muted-foreground">{label}</div>
      {sublabel && <div className="text-xs text-muted-foreground/70 mt-1">{sublabel}</div>}
    </div>
  )
}

export function MadeiraAgentContent() {
  return (
    <article className="prose-container">
      {/* Title Section */}
      <header className="mb-20">
        <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-4">
          Use Case Documentation
        </p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-6 text-balance">
          MADEIRA Agent
        </h1>
        <p className="text-xl sm:text-2xl text-muted-foreground leading-relaxed max-w-3xl">
          Multi-Agent Coordination System for Autonomous Execution
        </p>
        <div className="flex flex-wrap items-center gap-4 mt-8 pt-8 border-t border-border">
          <div className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">Reading time:</span> 8 min
          </div>
          <div className="w-1 h-1 rounded-full bg-muted-foreground/40" />
          <div className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">Version:</span> 0.6.0
          </div>
          <div className="w-1 h-1 rounded-full bg-muted-foreground/40" />
          <div className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">Last updated:</span> 2026
          </div>
        </div>
      </header>

      {/* Impact Metrics */}
      <section className="mb-20">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <MetricCard value="5" label="Specialized Agents" sublabel="Coordinated swarm" />
          <MetricCard value="4" label="Architecture Layers" sublabel="Separation of concerns" />
          <MetricCard value="93%" label="Task Completion" sublabel="Autonomous execution" />
          <MetricCard value="&lt;3" label="Retry Threshold" sublabel="Before escalation" />
        </div>
      </section>

      {/* Introduction */}
      <section id="overview" className="mb-20 scroll-mt-40">
        <span className="text-sm font-semibold text-accent-primary uppercase tracking-wider">
          01 — Overview
        </span>
        <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-4 mb-8">
          The Multi-Agent Platform
        </h2>
        
        <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
          <p>
            The MADEIRA Agent system implements a <strong className="text-foreground">coordinated multi-agent architecture</strong> that 
            decouples the reasoning engine from its tools and channels. This separation operates across four functional domains: 
            the Control Layer, Integration Layer, Execution Layer, and Intelligence Layer.
          </p>
          <p>
            This architecture addresses the fundamental problem of cognitive overload in AI systems. 
            Forcing a single agent to simultaneously architect a solution and execute the underlying 
            operations causes context degradation, inconsistent outputs, and execution failures. The 
            multi-agent platform separates concerns across specialized roles, each operating within 
            its own isolated context window.
          </p>
        </div>

        <KeyInsight>
          MADEIRA shifts from human-in-the-loop co-pilots to human-on-the-loop autonomous execution. 
          The system operates independently while humans maintain strategic oversight and intervention 
          capability at defined checkpoints.
        </KeyInsight>
      </section>

      {/* The Five Agents */}
      <section id="five-agents" className="mb-20 scroll-mt-40">
        <span className="text-sm font-semibold text-accent-primary uppercase tracking-wider">
          02 — Agent Architecture
        </span>
        <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-4 mb-8">
          Specialized Agent Roles
        </h2>
        
        <p className="text-lg text-muted-foreground leading-relaxed mb-10">
          Each agent operates with specific permissions and capabilities, enforced at the 
          configuration layer through role-based capability boundaries. This is not merely 
          prompt instruction—it is architectural constraint embedded in the system design.
        </p>

        <div className="grid gap-6 mb-10">
          <AgentCard
            name="Interface Agent"
            role="User Gateway"
            description="The user-facing operational assistant. Operates in read-only lookup mode, answers questions using deterministic retrieval patterns, and escalates multi-step tasks to the coordination layer. Never executes write operations directly."
            capabilities={["Intent Parsing", "Knowledge Retrieval", "User Interaction", "Task Escalation"]}
            color="teal"
          />
          <AgentCard
            name="Orchestrator"
            role="Coordinator"
            description="Receives user requests and decomposes them into discrete sub-tasks. Delegates to planning and execution agents, tracks progress across the workflow, and handles failure states. Cannot execute tasks directly—purely coordination logic."
            capabilities={["Task Decomposition", "Agent Delegation", "Progress Tracking", "Failure Handling"]}
            color="sage"
          />
          <AgentCard
            name="Architect"
            role="Planner"
            description="Operates in read-only mode using structured reasoning protocols. Produces detailed plan documents with explicit tool selections, dependency ordering, and risk assessments. Cannot write files or execute commands—planning only."
            capabilities={["Read-Only Analysis", "Plan Documents", "Risk Assessment", "Tool Selection"]}
            color="amber"
          />
          <AgentCard
            name="Executor"
            role="Builder"
            description="Operates in read-write mode. Reads the Architect's plan before taking any action. Executes strict, well-scoped directives with built-in error recovery loops guided by verification feedback."
            capabilities={["Code Execution", "File Operations", "System Calls", "Error Recovery"]}
            color="coral"
          />
          <AgentCard
            name="Verifier"
            role="Quality Gate"
            description="Validates Executor output via automated testing, static analysis, and behavioral verification. Diagnoses failures and suggests targeted fixes. Escalates to the Orchestrator after retry threshold is exceeded."
            capabilities={["Output Validation", "Automated Testing", "Failure Diagnosis", "Escalation"]}
            color="slate"
          />
        </div>
      </section>

      {/* Architecture */}
      <section id="architecture" className="mb-20 scroll-mt-40">
        <span className="text-sm font-semibold text-accent-primary uppercase tracking-wider">
          03 — System Architecture
        </span>
        <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-4 mb-8">
          Four-Layer Operating Model
        </h2>
        
        <div className="bg-gradient-to-br from-card/80 to-card/40 border border-border rounded-2xl p-8 mb-10">
          <div className="space-y-0">
            {[
              { layer: "Control Layer", desc: "Gateway routing, authentication, channel multiplexing", accent: "border-l-teal-500" },
              { layer: "Integration Layer", desc: "Channel adapters, protocol translation, message normalization", accent: "border-l-emerald-500" },
              { layer: "Execution Layer", desc: "Orchestrator → Architect → Executor → Verifier workflow", accent: "border-l-amber-500" },
              { layer: "Intelligence Layer", desc: "Memory persistence, workspace context, knowledge retrieval", accent: "border-l-rose-500" },
            ].map((item, i) => (
              <div key={i} className={`border-l-4 ${item.accent} pl-6 py-5 ${i < 3 ? 'border-b border-border/50' : ''}`}>
                <div className="font-semibold text-foreground text-lg">{item.layer}</div>
                <div className="text-muted-foreground text-sm mt-1">{item.desc}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="p-6 rounded-xl border border-border bg-card/50">
            <h3 className="text-lg font-semibold text-foreground mb-4">Behavioral Protocols</h3>
            <ul className="space-y-3 text-sm text-muted-foreground">
              <li className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-accent-primary mt-2 shrink-0" />
                <span><strong className="text-foreground">Self-Verification:</strong> Executor validates after every edit before proceeding</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-accent-primary mt-2 shrink-0" />
                <span><strong className="text-foreground">Loop Detection:</strong> Repetitive failures trigger escalation protocols</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-accent-primary mt-2 shrink-0" />
                <span><strong className="text-foreground">Memory Hygiene:</strong> All decisions persisted to workspace memory</span>
              </li>
            </ul>
          </div>
          <div className="p-6 rounded-xl border border-border bg-card/50">
            <h3 className="text-lg font-semibold text-foreground mb-4">Execution Guarantees</h3>
            <ul className="space-y-3 text-sm text-muted-foreground">
              <li className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-2 shrink-0" />
                <span><strong className="text-foreground">Plan-First:</strong> No execution without approved plan document</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-2 shrink-0" />
                <span><strong className="text-foreground">Bounded Retries:</strong> Maximum three attempts before human intervention</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-2 shrink-0" />
                <span><strong className="text-foreground">Audit Trail:</strong> Complete execution history with provenance</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Intelligence Tiers */}
      <section id="intelligence-tiers" className="mb-20 scroll-mt-40">
        <span className="text-sm font-semibold text-accent-primary uppercase tracking-wider">
          04 — Intelligence Routing
        </span>
        <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-4 mb-8">
          Adaptive Model Selection
        </h2>
        
        <div className="space-y-6 text-lg text-muted-foreground leading-relaxed mb-10">
          <p>
            The system supports seamless switching between intelligence tiers based on task complexity. 
            Every reasoning engine is assigned to exactly one tier, which determines its context capacity, 
            reasoning depth, and prompt configuration.
          </p>
          <p>
            This tiered approach optimizes for both <strong className="text-foreground">cost efficiency</strong> and <strong className="text-foreground">task quality</strong>. 
            Simple lookups route to lightweight models; complex multi-step reasoning routes to 
            high-capacity engines—all managed transparently by the orchestration layer.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {[
            { tier: "Compact", context: "16K", reasoning: "Basic", use: "Simple queries, lookups" },
            { tier: "Standard", context: "32K", reasoning: "Moderate", use: "Multi-step tasks" },
            { tier: "Extended", context: "128K", reasoning: "Advanced", use: "Complex reasoning" },
            { tier: "Maximum", context: "200K+", reasoning: "Deep", use: "Autonomous workflows" },
          ].map((item, i) => (
            <div key={i} className="p-5 rounded-xl border border-border bg-card/50 text-center">
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Tier {i + 1}</div>
              <div className="text-xl font-bold text-foreground mb-1">{item.tier}</div>
              <div className="text-2xl font-bold text-accent-primary mb-2">{item.context}</div>
              <div className="text-xs text-muted-foreground">{item.use}</div>
            </div>
          ))}
        </div>

        <KeyInsight>
          Prompt configurations are assembled from base templates plus tier-appropriate overlays. 
          Compact models receive streamlined instructions; maximum-tier models receive full feature sets 
          with extended reasoning protocols.
        </KeyInsight>
      </section>

      {/* Integration */}
      <section id="integration" className="mb-20 scroll-mt-40">
        <span className="text-sm font-semibold text-accent-primary uppercase tracking-wider">
          05 — Channel Integration
        </span>
        <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-4 mb-8">
          Multi-Channel Connectivity
        </h2>
        
        <p className="text-lg text-muted-foreground leading-relaxed mb-10">
          The Integration Layer connects MADEIRA agents to external systems through channel 
          adapters. Each adapter translates platform-specific events into the unified messaging 
          format, enabling consistent agent behavior across all communication channels.
        </p>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="p-6 rounded-xl border border-border bg-card/50">
            <h3 className="text-lg font-semibold text-foreground mb-4">Supported Channels</h3>
            <ul className="space-y-3 text-sm text-muted-foreground">
              <li className="flex items-center gap-3">
                <span className="w-2 h-2 rounded-full bg-accent-primary" />
                Real-time messaging platforms
              </li>
              <li className="flex items-center gap-3">
                <span className="w-2 h-2 rounded-full bg-accent-primary" />
                Team collaboration workspaces
              </li>
              <li className="flex items-center gap-3">
                <span className="w-2 h-2 rounded-full bg-accent-primary" />
                Mobile communication adapters
              </li>
              <li className="flex items-center gap-3">
                <span className="w-2 h-2 rounded-full bg-accent-primary" />
                Web dashboard interfaces
              </li>
            </ul>
          </div>
          <div className="p-6 rounded-xl border border-border bg-card/50">
            <h3 className="text-lg font-semibold text-foreground mb-4">Protocol Capabilities</h3>
            <ul className="space-y-3 text-sm text-muted-foreground">
              <li className="flex items-center gap-3">
                <span className="w-2 h-2 rounded-full bg-amber-500" />
                Context resources for application state
              </li>
              <li className="flex items-center gap-3">
                <span className="w-2 h-2 rounded-full bg-amber-500" />
                Tool invocation for agent actions
              </li>
              <li className="flex items-center gap-3">
                <span className="w-2 h-2 rounded-full bg-amber-500" />
                Template prompts for user workflows
              </li>
              <li className="flex items-center gap-3">
                <span className="w-2 h-2 rounded-full bg-amber-500" />
                Session management for continuity
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Getting Started */}
      <section id="deployment" className="scroll-mt-40">
        <span className="text-sm font-semibold text-accent-primary uppercase tracking-wider">
          06 — Deployment
        </span>
        <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-4 mb-8">
          Implementation Path
        </h2>
        
        <p className="text-lg text-muted-foreground leading-relaxed mb-10">
          MADEIRA Agent deployment follows a structured progression from development to 
          production. The architecture supports multiple deployment profiles, scaling from 
          local development environments to cloud-native production infrastructure.
        </p>

        <div className="space-y-6">
          {[
            { step: "1", title: "Environment Configuration", desc: "Define deployment profiles, credential management, and intelligence tier assignments" },
            { step: "2", title: "Agent Provisioning", desc: "Configure agent capabilities, behavioral protocols, and workspace boundaries" },
            { step: "3", title: "Channel Binding", desc: "Connect communication adapters and configure routing rules" },
            { step: "4", title: "Knowledge Integration", desc: "Link retrieval systems and configure memory persistence layers" },
            { step: "5", title: "Observability Setup", desc: "Enable tracing, monitoring, and quality telemetry collection" },
          ].map((item) => (
            <div key={item.step} className="flex items-start gap-5">
              <span className="flex-shrink-0 w-10 h-10 rounded-full bg-accent-primary/20 flex items-center justify-center text-sm font-bold text-accent-primary">
                {item.step}
              </span>
              <div>
                <p className="font-semibold text-foreground text-lg">{item.title}</p>
                <p className="text-muted-foreground mt-1">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </article>
  )
}
